import React, { useCallback, useEffect, useState } from "react";
import { Avatar, CardHeader, Chip, makeStyles, Tooltip } from "@material-ui/core";
import RestoreOutlinedIcon from "@material-ui/icons/RestoreOutlined";

import { i18n } from "../../translate/i18n";
import BezinhoImg from '../../assets/miscSvg/bezinho.svg';
import internalChatImg from "../../assets/miscPng/internalChat.png";
import internalGroupImg from "../../assets/miscPng/internalGroup.png";
import truncateString from "../../utils/truncateString";
import WhatsappOfficialImg from "../../assets/miscJpg/whatsappOfficial.jpg";

import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
	lidChip: {
		margin: 2,
		border: `2px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,
	},

	temporaryChatSetting: {
		position: "absolute",
		top: "40px",
		left: "75px",
		zIndex: 1,
	},

	internalChatHeaderContainer: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		gap: "1em",
	},

	customBadge: {
		border: `1px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,
	
		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		fontSize: "10px",
		width: "fit-content",
		display: "flex",
		gap: "3px",
		alignItems: "center",
	},
}));

const TicketInfo = ({
	contact,
	ticket,
	onClick,
	lastKnownPresenceCode = 0,
	lastSeenTimestamp = null
}) => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const [isLastSeenDateToday, setIsLastSeenDateToday] = useState(false);
	const [presenceMessage, setPresenceMessage] = useState("");
	const [viewPresenceUpdateSetting, setViewPresenceUpdateSetting] = useState("disabled");

	const { getSetting, isLoading } = useGlobalRegisters();

	const presenceText =
		viewPresenceUpdateSetting === "enabled"
			? lastSeenTimestamp === null
				? presenceMessage
				: `${isLastSeenDateToday ? i18n.t("ticketInfo.lastSeenToday") : i18n.t("ticketInfo.lastSeenAt")} ${presenceMessage}`
			: undefined;



	// 	***************
	// 	** Callbacks **
	// 	***************
	const checkIsLastSeenDateToday = useCallback(() => {
		const lastSeenDate = new Date(lastSeenTimestamp);
		const lastSeenDay = lastSeenDate.getDate();
		const lastSeenMonth = lastSeenDate.getMonth();
		const lastSeenYear = lastSeenDate.getFullYear();

		const currentDate = new Date();
		const currentDay = currentDate.getDate();
		const currentMonth = currentDate.getMonth();
		const currentYear = currentDate.getFullYear();

		const isLastSeenDateToday = currentDay === lastSeenDay
			&& currentMonth === lastSeenMonth
			&& currentYear === lastSeenYear;

		return isLastSeenDateToday;
	}, [lastSeenTimestamp]);

	const formatLastSeenPresenceMessage = useCallback((isTodayCheck) => {
		const lastSeenDate = new Date(lastSeenTimestamp);
			
		const hours = lastSeenDate.getHours().toString().padStart(2, "0");
		const minutes = lastSeenDate.getMinutes().toString().padStart(2, "0");
		const day = lastSeenDate.getDate().toString().padStart(2, "0");
		const month = (lastSeenDate.getMonth() + 1).toString().padStart(2, "0");
		const year = lastSeenDate.getFullYear().toString().slice(-2);

		return isTodayCheck ? `${hours}:${minutes}` : `${hours}:${minutes} ${day}/${month}/${year}`;
	}, [lastSeenTimestamp]);



	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		if (isLoading) return;

		// ***---- Fetch Settings ----***
		setViewPresenceUpdateSetting(getSetting("viewPresenceUpdate"));



		// ***---- Setting Presence Message ----***
		if (lastKnownPresenceCode === 1) {
			setPresenceMessage(i18n.t("ticketInfo.composing"));
			setIsLastSeenDateToday(false);
		}

		else if (lastKnownPresenceCode === 2) {
			setPresenceMessage(i18n.t("ticketInfo.recording"));
			setIsLastSeenDateToday(false);
		}

		else if (lastSeenTimestamp !== null) {
			const isTodayCheck = checkIsLastSeenDateToday();
			const formattedDate = formatLastSeenPresenceMessage(isTodayCheck);
			setIsLastSeenDateToday(isTodayCheck);
			setPresenceMessage(formattedDate);
		}

		else {
			setPresenceMessage(" ");
			setIsLastSeenDateToday(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, checkIsLastSeenDateToday, formatLastSeenPresenceMessage, lastKnownPresenceCode, lastSeenTimestamp]);



	// 	***************
	// 	** Functions **
	// 	***************
	const renderCardHeaderTitle = () => {
		const isContactChat = !ticket.contact?.isGroup;
		const isAddressingModeLid = ticket.contact?.addressingMode?.addressingMode === DefaultValuesUtils.ADDRESSING_MODES.lid;
		const ticketTitle = `${truncateString(contact?.name, 27)} #${ticket.id}`;

		if (isContactChat && isAddressingModeLid) {
			return (
				<>
					<span>{ticketTitle}</span>
					&nbsp;
					<Chip variant="outlined" label={i18n.t("tickets.notification.lid")} className={classes.lidChip} />
				</>
			);
		}

		return `${ticketTitle}`;
	};



	// 	************
	// 	** Return **
	// 	************
	return (
		<>
			{ticket.type === 0 && ticket.contact?.temporaryChatSetting?.isEnabled && (
				<Tooltip
					className={classes.temporaryChatSetting}
					title={i18n.t("tickets.notification.temporaryChat").replace("@", ticket.contact?.temporaryChatSetting?.duration)}
					placement="bottom-start"
					PopperProps={{ disablePortal: true }}
					arrow
				>
					<RestoreOutlinedIcon />
				</Tooltip>
			)}

			{ticket.type === 0 && (
				<CardHeader
					onClick={onClick}
					style={{ cursor: "pointer" }}
					titleTypographyProps={{ noWrap: true }}
					subheaderTypographyProps={{ noWrap: true }}
					avatar={<Avatar src={contact?.name === DefaultValuesUtils.SPECIAL_NAMES.whatsapp ? WhatsappOfficialImg : contact?.profilePicUrl} />}
					title={renderCardHeaderTitle()}
					subheader={presenceText}
				/>
			)}

			{ticket.type === 1 && (
				<CardHeader
					onClick={onClick}
					style={{ cursor: "pointer" }}
					titleTypographyProps={{ noWrap: true }}
					subheaderTypographyProps={{ noWrap: true }}
					avatar={<Avatar src={contact?.profilePicUrl} alt="contact_image" />}
					title={`${truncateString(ticket.noteName, 27)} #${ticket.id}`}
				/>
			)}

			{ticket.type === 2 && (
				<CardHeader
					onClick={onClick}
					style={{ cursor: "pointer" }}
					titleTypographyProps={{ noWrap: true }}
					subheaderTypographyProps={{ noWrap: true }}
					avatar={<Avatar src={BezinhoImg} alt="contact_image" />}
					title={`${truncateString(ticket.noteName, 27)} #${ticket.id}`}
				/>
			)}

			{ticket.type === 3 && (
				<CardHeader
					onClick={onClick}
					style={{ cursor: "pointer" }}
					titleTypographyProps={{ noWrap: true }}
					subheaderTypographyProps={{ noWrap: true }}
					avatar={<Avatar src={internalChatImg} alt="contact_image" />}
					title={
						<div className={classes.internalChatHeaderContainer}>
							{truncateString(ticket.noteName, 27)} #{ticket.id}
							<span className={classes.customBadge}>{ticket.user?.name} || {ticket.userInternalChat?.name}</span>
						</div>
					}
				/>
			)}

			{ticket.type === 4 && (
				<CardHeader
					onClick={onClick}
					style={{ cursor: "pointer" }}
					titleTypographyProps={{ noWrap: true }}
					subheaderTypographyProps={{ noWrap: true }}
					avatar={<Avatar src={internalGroupImg} alt="contact_image" />}
					title={`${truncateString(ticket.noteName, 27)} #${ticket.id}`}
				/>
			)}
		</>		
	);
};

export default TicketInfo;
